import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hasCardAction } from '../utils/permissionHelper';
import axiosInstance from '../api/axiosConfig';
import './MonthlyDocuments.css';
import receptionBg from '../assets/images/backgrounds/reception-bg.png';

const MonthlyDocuments = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [businessGroups, setBusinessGroups] = useState([]);
    const [branches, setBranches] = useState([]);
    const [selectedBusinessGroup, setSelectedBusinessGroup] = useState('');
    const [selectedBranch, setSelectedBranch] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [document, setDocument] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedDay, setSelectedDay] = useState(null);
    const [showMarkModal, setShowMarkModal] = useState(false);
    const [modalData, setModalData] = useState({ reportName: '', day: 0, currentStatus: '' });
    const [allPendingItems, setAllPendingItems] = useState([]);
    const [loadingPending, setLoadingPending] = useState(false);
    const [showNAModal, setShowNAModal] = useState(false);
    const [naReason, setNaReason] = useState('');

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    // Check user permissions
    const userPermissions = user?.permissions || { cards: [], actions: [] };
    const canMark = user?.role === 'system_admin' || 
                   hasCardAction(userPermissions, 'monthly-documents', 'create') || 
                   hasCardAction(userPermissions, 'monthly-documents', 'edit');
    const canVerify = user?.role === 'system_admin' || 
                     hasCardAction(userPermissions, 'monthly-documents', 'approve');

    // Fetch business groups on mount
    useEffect(() => {
        fetchBusinessGroups();
        if (canVerify) {
            fetchAllPendingVerifications();
        }
    }, []);

    // Fetch branches when business group changes
    useEffect(() => {
        if (selectedBusinessGroup) {
            fetchBranches(selectedBusinessGroup);
        }
    }, [selectedBusinessGroup]);

    // Auto-fetch document when all required fields are selected
    useEffect(() => {
        if (selectedBusinessGroup && selectedBranch && selectedMonth && selectedYear) {
            fetchDocument();
        }
    }, [selectedBusinessGroup, selectedBranch, selectedMonth, selectedYear]);

    const fetchBusinessGroups = async () => {
        try {
            const response = await axiosInstance.get('/api/monthly-docs/config');
            if (response.data.success) {
                setBusinessGroups(response.data.businessGroups);
            }
        } catch (err) {
            setError('Failed to fetch business groups');
        }
    };

    const fetchAllPendingVerifications = async () => {
        setLoadingPending(true);
        try {
            const response = await axiosInstance.get('/api/monthly-docs/pending-verification');
            if (response.data.success) {
                setAllPendingItems(response.data.pendingItems || []);
            }
        } catch (err) {
            console.error('Failed to fetch pending verifications:', err);
        } finally {
            setLoadingPending(false);
        }
    };

    const fetchBranches = async (businessGroup) => {
        try {
            const response = await axiosInstance.get(
                `/api/monthly-docs/config?businessGroup=${encodeURIComponent(businessGroup)}`
            );
            if (response.data.success) {
                setBranches(response.data.branches);
            }
        } catch (err) {
            setError('Failed to fetch branches');
        }
    };

    const fetchDocument = async () => {
        if (!selectedBusinessGroup || !selectedBranch) {
            setError('Please select business group and branch');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await axiosInstance.get(
                `/api/monthly-docs/document?businessGroup=${encodeURIComponent(selectedBusinessGroup)}&branch=${encodeURIComponent(selectedBranch)}&month=${selectedMonth}&year=${selectedYear}`
            );
            
            if (response.data.success) {
                setDocument(response.data.document);
            } else {
                setError(response.data.message || 'Failed to fetch document');
            }
        } catch (err) {
            setError('Failed to fetch document');
        } finally {
            setLoading(false);
        }
    };

    const handleDayClick = (reportName, day, dayStatus) => {
        // Validate date: Only allow marking for yesterday and earlier
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const clickedDate = new Date(selectedYear, selectedMonth - 1, day);
        clickedDate.setHours(0, 0, 0, 0);
        
        if (clickedDate >= today) {
            setError('Cannot mark reports for today or future dates. Reports can only be marked for yesterday and earlier.');
            setTimeout(() => setError(''), 5000);
            return;
        }
        
        setModalData({ reportName, day, dayStatus });
        setShowMarkModal(true);
    };

    const handleReceptionistAction = async (action, remarks) => {
        try {
            const response = await axiosInstance.post('/api/monthly-docs/mark-receptionist', {
                documentId: document._id,
                reportName: modalData.reportName,
                day: modalData.day,
                status: action === 'mark' ? 'RECEIVED' : 'NOT_RECEIVED',
                remarks
            });
            
            if (response.data.success) {
                setDocument(response.data.document);
                setShowMarkModal(false);
                setModalData({ reportName: '', day: 0, dayStatus: null });
            } else {
                setError(response.data.message || 'Failed to update');
            }
        } catch (err) {
            setError('Failed to update');
        }
    };

    const handleAccountsAction = async (action, remarks) => {
        try {
            const response = await axiosInstance.post('/api/monthly-docs/mark-accounts', {
                documentId: document._id,
                reportName: modalData.reportName,
                day: modalData.day,
                verify: action === 'verify',
                remarks
            });
            
            if (response.data.success) {
                setDocument(response.data.document);
                setShowMarkModal(false);
                setModalData({ reportName: '', day: 0, dayStatus: null });
            } else {
                setError(response.data.message || 'Failed to update');
            }
        } catch (err) {
            setError('Failed to update');
        }
    };

    const handleMarkNotApplicable = async () => {
        if (!naReason.trim()) {
            setError('Please provide a reason for marking as not applicable');
            setTimeout(() => setError(''), 3000);
            return;
        }

        try {
            const response = await axiosInstance.post('/api/monthly-docs/mark-not-applicable', {
                documentId: document._id,
                reportName: modalData.reportName,
                day: modalData.day,
                markAsNA: true,
                reason: naReason
            });
            
            if (response.data.success) {
                setDocument(response.data.document);
                setShowMarkModal(false);
                setShowNAModal(false);
                setNaReason('');
                setModalData({ reportName: '', day: 0, dayStatus: null });
            } else {
                setError(response.data.message || 'Failed to mark as not applicable');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to mark as not applicable');
        }
    };

    const handleUnmarkNotApplicable = async () => {
        try {
            const response = await axiosInstance.post('/api/monthly-docs/mark-not-applicable', {
                documentId: document._id,
                reportName: modalData.reportName,
                day: modalData.day,
                markAsNA: false,
                reason: ''
            });
            
            if (response.data.success) {
                setDocument(response.data.document);
                setShowMarkModal(false);
                setModalData({ reportName: '', day: 0, dayStatus: null });
            } else {
                setError(response.data.message || 'Failed to unmark not applicable');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to unmark not applicable');
        }
    };

    const getDaysInMonth = (month, year) => {
        return new Date(year, month, 0).getDate();
    };

    const formatMarkedDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[date.getMonth()]} ${date.getDate()}`;
    };

    const getPendingVerificationCount = (report) => {
        if (!report.dailyStatuses) return 0;
        
        return report.dailyStatuses.filter(ds => 
            ds.status === 'RECEIVED' && 
            ds.receptionistMarkedDate && 
            !ds.accountsMarkedDate
        ).length;
    };

    const getPendingItems = () => {
        if (!document) return [];
        const pending = [];
        document.reports.forEach(report => {
            report.dailyStatuses.forEach(ds => {
                if (ds.status === 'RECEIVED' && ds.receptionistMarkedDate && !ds.accountsMarkedDate) {
                    pending.push({
                        reportName: report.reportName,
                        day: ds.day,
                        date: new Date(document.year, document.month - 1, ds.day),
                        receptionistMarkedDate: ds.receptionistMarkedDate
                    });
                }
            });
        });
        return pending.sort((a, b) => a.day - b.day);
    };

    const getDocumentStatus = () => {
        if (!document) return null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        let verified = 0, pendingVerification = 0, notReceived = 0, total = 0;
        // notReceived counts report-days (one missing report on one day); the same day
        // can be missing several reports, so notReceivedDayCount is the distinct-day figure.
        const notReceivedDaySet = new Set();
        document.reports.forEach(report => {
            report.dailyStatuses.forEach(ds => {
                if (ds.status === 'NOT_APPLICABLE') return;
                const d = new Date(document.year, document.month - 1, ds.day);
                d.setHours(0, 0, 0, 0);
                if (d >= today) return;
                total++;
                if (ds.accountsMarkedDate) verified++;
                else if (ds.receptionistMarkedDate) pendingVerification++;
                else {
                    notReceived++;
                    notReceivedDaySet.add(ds.day);
                }
            });
        });
        return { total, verified, pendingVerification, notReceived, notReceivedDayCount: notReceivedDaySet.size };
    };

    const getNotReceivedDays = () => {
        if (!document) return [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return document.reports.map(report => ({
            reportName: report.reportName,
            days: report.dailyStatuses
                .filter(ds => {
                    if (ds.status !== 'NOT_RECEIVED') return false;
                    const d = new Date(document.year, document.month - 1, ds.day);
                    d.setHours(0, 0, 0, 0);
                    return d < today;
                })
                .map(ds => ds.day)
        })).filter(r => r.days.length > 0);
    };

    const handleQuickVerify = async (reportName, day) => {
        try {
            const response = await axiosInstance.post('/api/monthly-docs/mark-accounts', {
                documentId: document._id,
                reportName,
                day,
                verify: true,
                remarks: ''
            });
            
            if (response.data.success) {
                setDocument(response.data.document);
            } else {
                setError(response.data.message || 'Failed to verify');
            }
        } catch (err) {
            setError('Failed to verify');
        }
    };

    const handleQuickVerifyFromList = async (documentId, reportName, day) => {
        try {
            const response = await axiosInstance.post('/api/monthly-docs/mark-accounts', {
                documentId,
                reportName,
                day,
                verify: true,
                remarks: ''
            });
            
            if (response.data.success) {
                // Refresh the pending list
                await fetchAllPendingVerifications();
                // If this document is currently loaded, refresh it too
                if (document && document._id === documentId) {
                    setDocument(response.data.document);
                }
            } else {
                setError(response.data.message || 'Failed to verify');
            }
        } catch (err) {
            setError('Failed to verify');
        }
    };

    const renderCalendar = (report) => {
        const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
        const days = [];
        
        // Calculate today for comparison
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        for (let day = 1; day <= daysInMonth; day++) {
            const dayStatus = report.dailyStatuses?.find(ds => ds.day === day);
            const status = dayStatus?.status || 'NOT_RECEIVED';
            const isReceived = status === 'RECEIVED';
            const isNotApplicable = status === 'NOT_APPLICABLE';
            
            // Check who marked it
            const receptionistMarked = dayStatus?.receptionistMarkedDate;
            const accountsVerified = dayStatus?.accountsMarkedDate;
            
            // Check if this date is today or in the future (disabled)
            const dayDate = new Date(selectedYear, selectedMonth - 1, day);
            dayDate.setHours(0, 0, 0, 0);
            const isDisabled = dayDate >= today;
            
            // Determine visual state
            let statusClass = 'not-received';
            let statusIcon = '✗';
            let markedInfo = '';
            
            if (isNotApplicable) {
                // Not applicable - show gray with N/A
                statusClass = 'not-applicable';
                statusIcon = 'N/A';
                const reason = dayStatus?.notApplicableReason || 'Not Applicable';
                const isAuto = dayStatus?.isAutoMarkedNA ? ' (Auto)' : '';
                markedInfo = `${reason}${isAuto}`;
            } else if (isReceived && receptionistMarked) {
                if (accountsVerified) {
                    // Both marked - show green + orange ticks
                    statusClass = 'verified';
                    statusIcon = '✓✓';
                    markedInfo = `Verified ${formatMarkedDate(accountsVerified)}`;
                } else {
                    // Only receptionist marked - show green tick
                    statusClass = 'receptionist-marked';
                    statusIcon = '✓';
                    markedInfo = `Marked ${formatMarkedDate(receptionistMarked)}`;
                }
            }
            
            // Add disabled class for today and future dates
            if (isDisabled) {
                statusClass += ' disabled';
            }

            days.push(
                <div
                    key={day}
                    className={`calendar-day ${statusClass}`}
                    onClick={() => !isDisabled && handleDayClick(report.reportName, day, dayStatus)}
                    title={isDisabled ? 'Cannot mark reports for today or future dates' : markedInfo}
                    style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                >
                    <div className="day-number">{day}</div>
                    <div className="day-status">
                        {statusIcon}
                    </div>
                    {markedInfo && (
                        <div className="marked-info">
                            {markedInfo}
                        </div>
                    )}
                </div>
            );
        }

        return <div className="calendar-grid">{days}</div>;
    };

    return (
        <div className="monthly-documents-container" style={{ backgroundImage: `url(${receptionBg})` }}>
            <div className="monthly-content">
                <div className="page-header">
                    <img src="/logo.png" alt="Reception Logo" className="app-logo" />
                    <div className="page-header-text">
                        <h1>Daily Documents Tracking</h1>
                        <p>Document acknowledgement & verification system</p>
                    </div>
                    <div className="page-header-actions">
                        <div className="user-info">
                            <strong>{user?.fullName || user?.username}</strong>
                            <span className="user-role">({user?.role})</span>
                        </div>
                        <button
                            type="button"
                            className="btn-back"
                            onClick={() => navigate('/purpose-selection')}
                        >
                            ← Back
                        </button>
                    </div>
                </div>

                <div className="filters-card">
                    <div className="filters-grid">
                        <div className="filter-group">
                            <label>Business Group</label>
                            <select 
                                value={selectedBusinessGroup} 
                                onChange={(e) => setSelectedBusinessGroup(e.target.value)}
                                className="form-select"
                            >
                                <option value="">Select Business Group</option>
                                {businessGroups.map(bg => (
                                    <option key={bg} value={bg}>{bg}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Branch</label>
                            <select 
                                value={selectedBranch} 
                                onChange={(e) => setSelectedBranch(e.target.value)}
                                className="form-select"
                                disabled={!selectedBusinessGroup}
                            >
                                <option value="">Select Branch</option>
                                {branches.map(branch => (
                                    <option key={branch} value={branch}>{branch}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Month</label>
                            <select 
                                value={selectedMonth} 
                                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                className="form-select"
                            >
                                {months.map((month, index) => (
                                    <option key={index} value={index + 1}>{month}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Year</label>
                            <select 
                                value={selectedYear} 
                                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                className="form-select"
                            >
                                {[2024, 2025, 2026, 2027, 2028].map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="filters-actions">
                        <button 
                            className="btn btn-primary" 
                            onClick={fetchDocument}
                            disabled={loading || !selectedBusinessGroup || !selectedBranch}
                        >
                            {loading ? 'Loading...' : '🔄 Refresh Document'}
                        </button>
                    </div>
                </div>

                {error && <div className="error-message">{error}</div>}

                {canVerify && (
                    loadingPending ? (
                        <div className="pending-verification-card">
                            <p>Loading pending verifications...</p>
                        </div>
                    ) : allPendingItems.length > 0 ? (
                        <div className="pending-verification-card">
                            <div className="pending-header">
                                <h3>📋 Awaiting Your Verification ({allPendingItems.length})</h3>
                                <p className="pending-subtitle">
                                    Receptionist-marked reports not yet verified — current month, all branches
                                </p>
                            </div>
                            <div className="pending-list">
                                <table className="pending-table">
                                    <thead>
                                        <tr>
                                            <th>Business Group</th>
                                            <th>Branch</th>
                                            <th>Report</th>
                                            <th>Day</th>
                                            <th>Date</th>
                                            <th>Marked On</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allPendingItems.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.businessGroup}</td>
                                                <td>{item.branch}</td>
                                                <td>{item.reportName}</td>
                                                <td className="day-cell">{item.day}</td>
                                                <td>{new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                                                <td>{formatMarkedDate(item.receptionistMarkedDate)}</td>
                                                <td>
                                                    <button
                                                        className="btn-verify-quick"
                                                        onClick={() => handleQuickVerifyFromList(item.documentId, item.reportName, item.day)}
                                                    >
                                                        ✓ Verify
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div className="no-pending-card">
                            <p>✅ No pending verifications for current month</p>
                        </div>
                    )
                )}

                {document && (() => {
                    const status = getDocumentStatus();
                    const notReceivedDays = getNotReceivedDays();
                    const statusLabel = !status ? null
                        : status.notReceived > 0
                            ? { text: `${status.notReceivedDayCount} day${status.notReceivedDayCount > 1 ? 's' : ''} with missing reports`, cls: 'status-not-received' }
                            : status.pendingVerification > 0
                                ? { text: `${status.pendingVerification} pending verification`, cls: 'status-pending-verification' }
                                : { text: 'Completed', cls: 'status-completed' };
                    return (
                    <div className="document-card">
                        <div className="document-header">
                            <h2>{document.businessGroup} - {document.branch}</h2>
                            <h3>{months[document.month - 1]} {document.year}</h3>
                            {statusLabel && (
                                <div className={`approval-status ${statusLabel.cls}`}>
                                    {statusLabel.text}
                                </div>
                            )}
                        </div>

                        {notReceivedDays.length > 0 && (
                            <div className="not-received-summary">
                                <h4>⚠️ Reports not received</h4>
                                {notReceivedDays.map((r, i) => (
                                    <div key={i} className="not-received-row">
                                        <span className="not-received-report">{r.reportName}</span>
                                        <div className="not-received-days">
                                            {r.days.map(day => (
                                                <span key={day} className="not-received-day-chip">{day}</span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="legend">
                            <div className="legend-item">
                                <span className="legend-icon not-received">✗</span>
                                <span>Not Received</span>
                            </div>
                            <div className="legend-item">
                                <span className="legend-icon receptionist-marked">✓</span>
                                <span>Receptionist Marked</span>
                            </div>
                            <div className="legend-item">
                                <span className="legend-icon verified">✓✓</span>
                                <span>Accounts Verified</span>
                            </div>
                            <div className="legend-item">
                                <span className="legend-icon not-applicable">N/A</span>
                                <span>Not Applicable</span>
                            </div>
                        </div>

                        <div className="reports-list">
                            {document.reports.map((report, index) => {
                                const pendingCount = getPendingVerificationCount(report);
                                return (
                                    <div key={index} className="report-card">
                                        <div className="report-header">
                                            <h3 className="report-title">{report.reportName}</h3>
                                            {canVerify && pendingCount > 0 && (
                                                <div className="pending-badge">
                                                    {pendingCount} pending verification
                                                </div>
                                            )}
                                        </div>
                                        {renderCalendar(report)}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    );
                })()}

                {showMarkModal && (
                    <div className="modal-overlay" onClick={() => setShowMarkModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <h3>Day {modalData.day} - {modalData.reportName}</h3>
                            
                            {/* Show NOT_APPLICABLE status */}
                            {modalData.dayStatus?.status === 'NOT_APPLICABLE' ? (
                                <>
                                    <p className="modal-info">
                                        <strong>Status:</strong> Not Applicable
                                    </p>
                                    <p className="modal-info">
                                        <strong>Reason:</strong> {modalData.dayStatus.notApplicableReason || 'N/A'}
                                    </p>
                                    {modalData.dayStatus.isAutoMarkedNA && (
                                        <p className="modal-warning">
                                            ⚠️ This is an automatically configured weekly off day and cannot be changed
                                        </p>
                                    )}
                                    
                                    <div className="modal-actions">
                                        {!modalData.dayStatus.isAutoMarkedNA && canMark && (
                                            <button 
                                                className="btn-warning"
                                                onClick={handleUnmarkNotApplicable}
                                            >
                                                Remove N/A Status
                                            </button>
                                        )}
                                        <button 
                                            className="btn-secondary"
                                            onClick={() => setShowMarkModal(false)}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </>
                            ) : canMark && !canVerify ? (
                                <>
                                    <p className="modal-info">
                                        {modalData.dayStatus?.receptionistMarkedDate 
                                            ? `Marked as RECEIVED on ${formatMarkedDate(modalData.dayStatus.receptionistMarkedDate)}`
                                            : 'Not yet marked'}
                                    </p>
                                    
                                    {modalData.dayStatus?.accountsMarkedDate && (
                                        <p className="modal-warning">
                                            ⚠️ This day has been verified by accounts and cannot be unmarked
                                        </p>
                                    )}
                                    
                                    <div className="modal-actions">
                                        {!modalData.dayStatus?.receptionistMarkedDate ? (
                                            <>
                                                <button 
                                                    className="btn-success"
                                                    onClick={() => handleReceptionistAction('mark', '')}
                                                >
                                                    ✓ Mark as Received
                                                </button>
                                                <button 
                                                    className="btn-na"
                                                    onClick={() => setShowNAModal(true)}
                                                >
                                                    Mark as N/A
                                                </button>
                                            </>
                                        ) : !modalData.dayStatus?.accountsMarkedDate ? (
                                            <>
                                                <button 
                                                    className="btn-danger"
                                                    onClick={() => handleReceptionistAction('unmark', '')}
                                                >
                                                    ✗ Unmark
                                                </button>
                                                <button 
                                                    className="btn-na"
                                                    onClick={() => setShowNAModal(true)}
                                                >
                                                    Mark as N/A
                                                </button>
                                            </>
                                        ) : null}
                                        
                                        <button 
                                            className="btn-secondary"
                                            onClick={() => setShowMarkModal(false)}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </>
                            ) : canVerify ? (
                                <>
                                    <p className="modal-info">
                                        {modalData.dayStatus?.receptionistMarkedDate 
                                            ? `Receptionist marked on ${formatMarkedDate(modalData.dayStatus.receptionistMarkedDate)}`
                                            : 'Not marked by receptionist'}
                                    </p>
                                    
                                    {modalData.dayStatus?.accountsMarkedDate && (
                                        <p className="modal-info">
                                            Verified on {formatMarkedDate(modalData.dayStatus.accountsMarkedDate)}
                                        </p>
                                    )}
                                    
                                    <div className="modal-actions">
                                        {modalData.dayStatus?.receptionistMarkedDate && modalData.dayStatus?.status === 'RECEIVED' ? (
                                            <>
                                                {!modalData.dayStatus?.accountsMarkedDate ? (
                                                    <button 
                                                        className="btn-success"
                                                        onClick={() => handleAccountsAction('verify', '')}
                                                    >
                                                        ✓ Verify (Add Orange Tick)
                                                    </button>
                                                ) : (
                                                    <button 
                                                        className="btn-warning"
                                                        onClick={() => handleAccountsAction('unverify', '')}
                                                    >
                                                        Remove Verification
                                                    </button>
                                                )}
                                            </>
                                        ) : (
                                            <p className="modal-warning">
                                                Can only verify days marked as RECEIVED by receptionist
                                            </p>
                                        )}
                                        
                                        <button 
                                            className="btn-na"
                                            onClick={() => setShowNAModal(true)}
                                        >
                                            Mark as N/A
                                        </button>
                                        
                                        <button 
                                            className="btn-secondary"
                                            onClick={() => setShowMarkModal(false)}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="modal-warning">
                                        You do not have permission to mark or verify reports.
                                    </p>
                                    <div className="modal-actions">
                                        <button 
                                            className="btn-secondary"
                                            onClick={() => setShowMarkModal(false)}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* N/A Reason Modal */}
                {showNAModal && (
                    <div className="modal-overlay" onClick={() => setShowNAModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <h3>Mark Day {modalData.day} as Not Applicable</h3>
                            <p className="modal-info">
                                Please provide a reason for marking this day as not applicable
                            </p>
                            
                            <div className="form-group">
                                <label>Reason:</label>
                                <select 
                                    value={naReason}
                                    onChange={(e) => setNaReason(e.target.value)}
                                    className="form-select"
                                >
                                    <option value="">Select a reason...</option>
                                    <option value="Public Holiday">Public Holiday</option>
                                    <option value="Branch Closed">Branch Closed</option>
                                    <option value="Staff Leave">Staff Leave</option>
                                    <option value="Emergency Closure">Emergency Closure</option>
                                    <option value="Other">Other</option>
                                </select>
                                
                                {naReason === 'Other' && (
                                    <input
                                        type="text"
                                        placeholder="Enter custom reason..."
                                        className="form-input"
                                        onChange={(e) => setNaReason(e.target.value)}
                                        style={{ marginTop: '10px' }}
                                    />
                                )}
                            </div>
                            
                            <div className="modal-actions">
                                <button 
                                    className="btn-success"
                                    onClick={handleMarkNotApplicable}
                                    disabled={!naReason}
                                >
                                    Confirm
                                </button>
                                <button 
                                    className="btn-secondary"
                                    onClick={() => {
                                        setShowNAModal(false);
                                        setNaReason('');
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MonthlyDocuments;
